/**
 * UserMenu.tsx — account avatar + sign-out control (top-right of the shell).
 *
 * A circular initials avatar button opens a small disclosure panel showing the
 * org/app name, the signed-in account's display name + email, and a "Sign out"
 * action. This is the fix for E0-BO-SIGN-OUT-CONTROL: app-role/access changes
 * only land in a fresh token, and until now there was no in-product way to
 * force one — signOut clears the MSAL cache and redirects through Entra.
 *
 * MSAL-coupled (reads useMsal()), so it is intentionally NOT rendered inside
 * AppShell (which stays MSAL-free for isolated testing) — App.tsx slots it in
 * via AppShell's `userMenu` prop.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';

const getInitials = (name: string | undefined, fallback: string): string => {
  const source = name?.trim() || fallback;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
};

export const UserMenu = () => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? instance.getActiveAccount() ?? undefined;
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    // Focus moves into the panel on open; Escape and outside-click close it
    // and return focus to the trigger (WCAG 2.4.3 focus order / 2.1.2 no trap).
    panelRef.current?.querySelector<HTMLElement>('button')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (
        panelRef.current?.contains(event.target as Node) ||
        buttonRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const name = account?.name;
  const email = account?.username;
  const initials = getInitials(name, email ?? '');

  const handleSignOut = () => {
    // loggerCallback in msalConfig already surfaces MSAL errors; this catch
    // only prevents an unhandled-rejection warning on the redirect promise.
    instance.logoutRedirect().catch(() => {});
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        data-testid="user-menu-button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('auth.signedInAs', { name: name ?? email ?? '' })}
        onClick={() => setOpen((value) => !value)}
        className="flex h-9 w-9 items-center max-md:h-11 max-md:w-11 justify-center rounded-full bg-gold font-body text-meta font-semibold text-on-gold hover:bg-gold-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
      >
        {initials}
      </button>
      {open ? (
        <div
          id={panelId}
          ref={panelRef}
          role="menu"
          aria-label={t('auth.menuTitle')}
          data-testid="user-menu-panel"
          className="absolute right-0 z-30 mt-2 w-72 rounded-xl bg-surface-2 p-4 text-body ring-1 ring-inset ring-ink-3/20"
        >
          <p className="text-meta font-semibold uppercase tracking-wide text-ink-3">
            {t('app.title')}
          </p>
          <p className="mt-2 text-body text-ink-1">{name}</p>
          <p className="text-meta text-ink-3">{email}</p>
          <button
            type="button"
            role="menuitem"
            data-testid="sign-out-button"
            onClick={handleSignOut}
            className="mt-4 w-full rounded-md bg-surface-3 px-3 py-2 text-left text-body text-ink-1 hover:bg-surface-1 focus:outline-none focus:ring-2 focus:ring-outline-focus"
          >
            {t('auth.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
};
